import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventTaskCard } from '../components/EventTaskCard';
import { EventTaskModal } from '../components/EventTaskModal';
import { CreateEventTaskModal } from '../components/CreateEventTaskModal';
import { useAuth } from '../contexts/AuthContext';

interface TaskCard {
  id: string;
  icon: string;
  label: string;
  description: string;
  color: string;
}

const TASK_CARDS: TaskCard[] = [
  {
    id: 'article-review',
    icon: '📖',
    label: 'View My Articles for Review',
    description: 'Review assigned booklet articles',
    color: '#6a1b9a',
  },
];

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1a6b3c',
    marginBottom: '0.25rem',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: '#666',
    marginBottom: '1.5rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '0.75rem',
  },
  button: {
    padding: '0.85rem 1rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    transition: 'all 0.2s',
    textAlign: 'left' as const,
    width: '100%',
  },
  icon: {
    fontSize: '1.5rem',
    lineHeight: 1,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: '2px',
  },
  description: {
    fontSize: '0.75rem',
    color: '#666',
    lineHeight: 1.3,
  },
  toast: {
    position: 'fixed' as const,
    bottom: '24px',
    right: '24px',
    backgroundColor: '#2e7d32',
    color: '#fff',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 500,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 1100,
    maxWidth: '360px',
  },
};

export function MyTasksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [eventTaskModalOpen, setEventTaskModalOpen] = useState(false);
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  const [eventTaskRefreshKey, setEventTaskRefreshKey] = useState(0);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const canCreateTasks = user && ['Delegate', 'Organizer', 'Admin'].includes(user.role);

  const handleRefreshEventTasks = useCallback(() => {
    setEventTaskRefreshKey((k) => k + 1);
  }, []);

  return (
    <div style={isMobile ? { ...styles.page, padding: '1rem' } : styles.page}>
      <h1 style={styles.title}>My Tasks</h1>
      <p style={styles.subtitle}>Your assigned tasks and review items</p>

      <div style={isMobile ? { ...styles.grid, gridTemplateColumns: '1fr' } : styles.grid}>
        {TASK_CARDS.map((card) => (
          <button
            key={card.id}
            ref={(el) => { buttonRefs.current[card.id] = el; }}
            style={styles.button}
            onClick={() => navigate('/weekend-booklet?tab=submissions&view=my-reviews')}
            onMouseEnter={(e) => {
              const el = e.currentTarget;
              el.style.borderColor = card.color;
              el.style.boxShadow = `0 2px 8px ${card.color}22`;
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget;
              el.style.borderColor = '#e0e0e0';
              el.style.boxShadow = 'none';
            }}
            aria-label={`${card.label}: ${card.description}`}
          >
            <span style={styles.icon}>{card.icon}</span>
            <div style={styles.content}>
              <div style={styles.label}>{card.label}</div>
              <div style={styles.description}>{card.description}</div>
            </div>
          </button>
        ))}
      </div>

      <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1a6b3c', marginTop: '2rem', marginBottom: '0.75rem' }}>
        Event Tasks
      </h2>
      <div style={isMobile ? { ...styles.grid, gridTemplateColumns: '1fr' } : styles.grid}>
        <EventTaskCard
          onOpen={() => setEventTaskModalOpen(true)}
          refreshKey={eventTaskRefreshKey}
        />
        {canCreateTasks && (
          <button
            style={styles.button}
            onClick={() => setCreateTaskModalOpen(true)}
            onMouseEnter={(e) => {
              const el = e.currentTarget;
              el.style.borderColor = '#1a6b3c';
              el.style.boxShadow = '0 2px 8px #1a6b3c22';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget;
              el.style.borderColor = '#e0e0e0';
              el.style.boxShadow = 'none';
            }}
            aria-label="Create a new event task"
          >
            <span style={styles.icon}>➕</span>
            <div style={styles.content}>
              <div style={styles.label}>Create Task</div>
              <div style={styles.description}>Assign a new task to your subcommittee</div>
            </div>
          </button>
        )}
      </div>

      {/* EventTaskModal */}
      <EventTaskModal
        isOpen={eventTaskModalOpen}
        onClose={() => {
          setEventTaskModalOpen(false);
          handleRefreshEventTasks();
        }}
      />

      {/* CreateEventTaskModal */}
      <CreateEventTaskModal
        isOpen={createTaskModalOpen}
        onClose={() => setCreateTaskModalOpen(false)}
        onSuccess={() => {
          handleRefreshEventTasks();
          setSuccessMessage('Event task created successfully');
          setTimeout(() => setSuccessMessage(''), 4000);
        }}
      />

      {successMessage && (
        <div
          style={styles.toast}
          role="status"
          aria-live="polite"
          onClick={() => setSuccessMessage('')}
        >
          ✓ {successMessage}
        </div>
      )}
    </div>
  );
}
